import React, { useState } from "react";
import { API } from "@/context/AuthContext";
import { Eye, EyeOff } from "lucide-react";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Email/password credentials form for both login and register modes.
export const EmailPasswordForm = ({ mode, onSuccess }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && form.name.trim().length < 2) {
      setError("Please enter your full name");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = mode === "register" ? "register" : "login";
      const payload = mode === "register"
        ? { name: form.name.trim(), email: form.email.trim(), password: form.password }
        : { email: form.email.trim(), password: form.password };
      const res = await fetch(`${API}/auth/${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
        return;
      }
      onSuccess(data);
    } catch {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {mode === "register" && (
        <div>
          <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Full Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your full name"
            className="w-full border border-[#E4E4E1] bg-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#0F172A]"
            data-testid="auth-name-input"
          />
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Email</label>
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="you@example.com"
          className="w-full border border-[#E4E4E1] bg-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#0F172A]"
          data-testid="auth-email-input"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Password</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            required
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
            className="w-full border border-[#E4E4E1] bg-white text-sm px-3 py-2.5 pr-10 rounded-sm focus:outline-none focus:border-[#0F172A]"
            data-testid="auth-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#0F172A]"
            data-testid="auth-toggle-password"
          >
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" data-testid="auth-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full btn-primary py-3 text-sm disabled:opacity-60"
        data-testid="auth-submit-btn"
      >
        {submitting ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
      </button>
    </form>
  );
};
